import Rollbar, {} from "rollbar";
import { offline } from "./network";
import { NOOP } from "../constants";
import { shouldReportError, shouldReportMessage } from "./error";
import { SECRET_KEYS } from "./redact";
let rollbar;
// Pull the strings Rollbar would fingerprint on out of a payload: the exception class/message of
// the primary error for trace items, the body for plain message items. Only the primary error is
// inspected - `trace_chain[0]`, with any causes following it - because a real failure wrapped
// around an expected cause is still a real failure and must be reported.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function rollbarPayloadError(payload) {
    const body = payload?.body;
    const exception = (body?.trace_chain?.[0] ?? body?.trace)?.exception;
    if (exception != null) {
        return {
            name: typeof exception.class === "string" ? exception.class : "",
            message: typeof exception.message === "string" ? exception.message : "",
        };
    }
    return {
        name: "",
        message: typeof body?.message?.body === "string" ? body.message.body : "",
    };
}
// Rollbar hands `checkIgnore` the original arguments alongside the payload, and for an unhandled
// rejection those include the rejection reason itself. That matters for anything thrown in the
// worker: it crosses the boundary as JSON, so the reason arrives as a plain object rather than an
// Error, Rollbar cannot read an exception class off it and files the item as "(unknown): message"
// with no class at all. `name` and `code` are exactly what most of `shouldReportError`'s rules
// are keyed on - session expiry, the 502-504 range, retry-exhausted - so reading them off the
// payload alone silently loses every one of those. Recover the reason and filter on that.
// Exported for testing.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function uncaughtReason(args) {
    if (!Array.isArray(args))
        return undefined;
    return args.find((arg) => arg != null &&
        typeof arg === "object" &&
        (typeof arg.name === "string" || typeof arg.message === "string"));
}
// The innermost frame of the primary error: Rollbar frames are ordered outermost first, so the
// throw site is last.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function throwSiteFilename(payload) {
    const body = payload?.body;
    const frames = (body?.trace_chain?.[0] ?? body?.trace)?.frames;
    if (!Array.isArray(frames) || frames.length === 0)
        return undefined;
    const filename = frames[frames.length - 1]?.filename;
    return typeof filename === "string" ? filename : undefined;
}
// True when the throw site is browser-extension code: the error was thrown by an extension (CSP
// violations from injected wasm, wallet inpage scripts, ...), not us.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function thrownByExtension(payload) {
    const filename = throwSiteFilename(payload);
    return filename !== undefined && /^(chrome|moz|safari-web)-extension:\/\//.test(filename);
}
// True when the page has no body, so the app, which mounts into it, isn't on screen. Automated
// browsers (crawlers, link-preview renderers) have been seen removing the body after the app
// mounted, and the next component that binds to it throws (#9932). There's no user to fix that
// for. A worker has no document and is unaffected.
function documentHasNoBody() {
    return typeof document !== "undefined" && document.body === null;
}
// True when the throw site is the HTML document itself rather than a script file: in-app browsers
// (Google Search App above all) inject scripts inline, and their stack overflows and minified
// `Error: La` throws carry the page URL as the filename, whichever route was open. Every script
// of ours has a file extension; a route path has none. The one inline snippet in index.html is a
// two-line global shim that runs before anything else.
// Exported for testing.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function thrownByDocumentScript(payload) {
    const filename = throwSiteFilename(payload);
    if (filename === undefined || !/^https?:\/\//i.test(filename))
        return false;
    try {
        return !/\.[a-z0-9]+$/i.test(new URL(filename).pathname);
    }
    catch {
        return false;
    }
}
// True for our CSP refusing an eval in code with no script URL anywhere on its stack: content
// scripts and in-app browsers inject code that evals, and Safari reports its frames as bare
// `:234:30` (Rollbar #29957). Every frame is checked, not just the throw site: Safari keeps
// builtins such as `Function@[native code]` as the last frame, so an eval refused in our own
// bundle ends in a frame with no URL too, and must still be reported via the frames above it.
// Exported for testing.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function evalRefusedInInjectedCode(payload) {
    if (rollbarPayloadError(payload).name !== "EvalError")
        return false;
    const body = payload?.body;
    const frames = (body?.trace_chain?.[0] ?? body?.trace)?.frames;
    if (!Array.isArray(frames) || frames.length === 0)
        return false;
    return frames.every((f) => typeof f?.filename === "string" && !/^[a-z-]+:\/\//i.test(f.filename));
}
// Rollbar matches an uploaded source map to a stack frame by exact minified URL. The same bundle
// is served from four origins - oc.app, webtest.oc.app, the canister's own .icp0.io domain, and
// http://tauri.localhost in the native app - and the workers are loaded with a `?v=` cache
// buster, so a frame's filename is one of many strings for the same file. `dynamichost` is
// Rollbar's placeholder host for exactly this: rewrite every frame to it and one uploaded map
// covers all four. `scripts/upload-source-maps.mjs` registers the same URLs.
// Frames that are not http(s) are left alone - `thrownByExtension` identifies extension code by
// the `chrome-extension://` prefix, and rewriting those would break that check.
const DYNAMIC_HOST = "http://dynamichost";
function normaliseFrameFilename(filename) {
    // tauri: is iOS, which serves the bundle from tauri://localhost (see navigation.rs); Android
    // uses http://tauri.localhost and is covered by https?
    if (typeof filename !== "string" || !/^(https?|tauri):\/\//i.test(filename))
        return undefined;
    try {
        // pathname only: drops the origin and the `?v=` query, keeping any directory prefix so
        // the URL still matches the map's path relative to the build directory
        return `${DYNAMIC_HOST}${new URL(filename).pathname}`;
    }
    catch {
        return undefined;
    }
}
// Exported for testing: a mismatch between this and the URLs `upload-source-maps.mjs` registers
// fails silently, with traces simply staying minified.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function normaliseSourceMapUrls(payload) {
    const body = payload?.body;
    const traces = body?.trace_chain ?? (body?.trace != null ? [body.trace] : []);
    if (!Array.isArray(traces))
        return;
    for (const trace of traces) {
        const frames = trace?.frames;
        if (!Array.isArray(frames))
            continue;
        for (const frame of frames) {
            const normalised = normaliseFrameFilename(frame?.filename);
            if (normalised !== undefined) {
                frame.filename = normalised;
            }
        }
    }
}
export function inititaliseLogger(apikey, version, env) {
    if (env === "production") {
        rollbar = Rollbar.init({
            accessToken: apikey,
            captureUncaught: true,
            autoInstrument: false,
            logLevel: "error",
            environment: env,
            enabled: env === "production",
            captureUnhandledRejections: true,
            // Added to Rollbar's defaults. Requests are redacted before they are logged, this
            // catches any which are not.
            scrubFields: [...SECRET_KEYS],
            // Noise with no fix on our side: opaque cross-origin "Script error." (injected
            // scripts, extensions), and Chrome extension messaging failures
            ignoredMessages: [
                "Script error.",
                "Could not establish connection. Receiving end does not exist.",
            ],
            // captureUncaught / captureUnhandledRejections bypass our logger, so uncaught
            // items get the same noise filtering at the transport layer. Logger-reported items
            // (isUncaught false) already passed shouldReportError and are not re-filtered here.
            checkIgnore: (isUncaught, args, payload) => {
                if (!isUncaught)
                    return false;
                if (thrownByExtension(payload) ||
                    thrownByDocumentScript(payload) ||
                    evalRefusedInInjectedCode(payload))
                    return true;
                // Prefer the reason itself: it still carries name and code, which the payload
                // does not for anything that crossed the worker boundary
                const reason = uncaughtReason(args);
                if (reason !== undefined)
                    return !shouldReportError(reason);
                const { name, message } = rollbarPayloadError(payload);
                return !shouldReportMessage(name, message);
            },
            transform: (payload) => normaliseSourceMapUrls(payload),
            payload: {
                environment: env,
                client: {
                    javascript: {
                        source_map_enabled: true,
                        code_version: version,
                        guess_uncaught_frames: true,
                    },
                },
            },
        });
    }
    return {
        error(message, error, ...optionalParams) {
            if (!shouldReportError(error) || documentHasNoBody()) {
                console.debug("Expected failure (not reported): ", message, error);
                return;
            }
            console.error(message, error, optionalParams);
            if (!offline()) {
                rollbar?.error(error, message, optionalParams);
            }
        },
        log(message, ...optionalParams) {
            console.log(message, optionalParams);
        },
        debug(message, ...optionalParams) {
            console.debug(message, optionalParams);
        },
    };
}
const DEFAULT_DEBUG = console.debug;
const DEFAULT_LOG = console.log;
const DEFAULT_WARN = console.warn;
export function setMinLogLevel(level) {
    const levelAsInt = level === "debug" ? 0 : level === "log" ? 1 : level === "warn" ? 2 : 3;
    const debugEnabled = levelAsInt <= 0;
    const logEnabled = levelAsInt <= 1;
    const warnEnabled = levelAsInt <= 2;
    console.debug = debugEnabled ? DEFAULT_DEBUG : NOOP;
    console.log = logEnabled ? DEFAULT_LOG : NOOP;
    console.warn = warnEnabled ? DEFAULT_WARN : NOOP;
}
export function debug(data, msg) {
    if (msg) {
        console.log(msg, data);
    }
    else {
        console.log(data);
    }
    return data;
}
export function logDuration(msg, started) {
    console.debug(`PERF: ${msg}`, Date.now() - started);
}
